import { createStore, compose, applyMiddleware, StoreEnhancer, Middleware, Store } from 'redux'
import { devToolsEnhancer } from 'redux-devtools-extension/logOnlyInProduction'
import createSagaMiddleware from 'redux-saga'

import { persistStore, autoRehydrate, LocalForageStorage } from 'redux-persist'

import rootSaga from './sagas'
import { setAuthConfig } from '../auth'
import platform from '../platform'

import { persistReadyAction } from './actions'
import { StoreState as RootStoreState, reducer } from './reducer'

import * as routing from 'onsenui-react-redux-navigator'
import { captureException } from '@sentry/core'

import IndexedDBStorage from './persist-localforage'
import { isIndexedDBError } from './indexeddb'

import { loggedOut } from 'modules/auth/actions'
import { NOT_LOGGED_IN_ERROR_MESSAGE } from './errors'

export type { StoreState as RootStoreState } from './reducer'

export const routingStoreStateSelector = (state: RootStoreState) => state.routing as routing.RoutingStoreState

export let store: Store<RootStoreState>

const PERSIST_KEY_PREFIX = 'react-redux-typescript-pattern'

export async function init(): Promise<void> {
	/**
	 * Create the redux-saga middleware.
	 */
	const sagaMiddleware = createSagaMiddleware({
		onError: (error, info) => {
			if (error.message === NOT_LOGGED_IN_ERROR_MESSAGE) {
				store.dispatch(loggedOut())
			} else {
				console.error(`Caught saga error: ${error}`, info)
				captureException(error)
				platform.alert(`An internal error has occurred: ${error.message}`)
			}
		},
	})

	const persistStorage = IndexedDBStorage('Maqasa')

	/* Substates that were persisted by previous versions of the app but no longer are; their stale records are
	   removed from storage after rehydration.
	 */
	const NO_LONGER_PERSISTED = ['routing', 'project', 'product', 'offline']

	let middlewares: Middleware[] = [sagaMiddleware]
	middlewares = platform.customiseReduxMiddleware(middlewares)

	/**
	 * Enhancers for the store.
	 */
	const enhancers = compose(
		/* Merge rehydrated persisted state into the store */
		autoRehydrate<RootStoreState>(),
		/* Add the middlewares */
		// eslint-disable-next-line prefer-spread
		applyMiddleware.apply(null, middlewares),
		/* Include the devtools. Comment this out if you don't want to use the dev tools. */
		devToolsEnhancer({}),
	) as StoreEnhancer<RootStoreState>

	/**
	 * Create the store. We do not include an initial state, as each of the module / duck
	 * reducers includes its own initial state.
	 */
	store = createStore(reducer, enhancers)

	/* Run the root saga */
	sagaMiddleware.run(rootSaga)

	/* Rehydrate the persisted state, and persist future changes to the store.
	   Firestore's persistent local cache is the offline store for all of the project data, so we only
	   persist the small substates that exist nowhere else: the photo upload queue, downloaded user and
	   combobox data, and the database module's user bookkeeping. The project and product substates in
	   particular are large, were expensive to reserialize whenever they changed, and are rebuilt from
	   Firestore's cache on demand. Not persisting routing means the app starts at the projects list.
	 */
	persistStore(store, {
		whitelist: ['database', 'user', 'combobox', 'storage'],
		keyPrefix: PERSIST_KEY_PREFIX,
		debounce: 1000,
		/* localforage's method signatures are looser than redux-persist's LocalForageStorage interface
		   (nullable getItem, setItem returning the value), but they are runtime-compatible.
		 */
		storage: persistStorage as unknown as LocalForageStorage,
	}, () => {
		/* Let our app know that the application state has been rehydrated and is ready to be used. */
		store.dispatch(persistReadyAction())

		for (const key of NO_LONGER_PERSISTED) {
			persistStorage.removeItem(`${PERSIST_KEY_PREFIX}${key}`).catch((error) => {
				console.warn(`Failed to remove stale persisted state for ${key}`, error)
			})
		}
	})

	/* Create the authentication config */
	setAuthConfig(platform.createAuthConfiguration())
}

window.addEventListener('unhandledrejection', function(ev) {
	const reason = `${ev.reason}`
	if (reason === 'popPage is already running.') {
		/* Ignoring known error from Onsen */
		ev.preventDefault()
		return
	}

	if (isIndexedDBError(ev.reason)) {
		/* The database module watches for these errors and recovers from them. An alert here would show
		   a message that the user cannot act on, before the message that tells them what happens next.
		 */
		console.error('An IndexedDB error occurred', ev.reason)
		captureException(ev.reason)
		ev.preventDefault()
		return
	}

	if (typeof ev.reason === 'object' && ev.reason instanceof Error) {
		if (ev.reason.message.indexOf('auth/network-request-failed') !== -1) {
			/* Ignore Firebase networking errors... they are transient network errors.
			   The message we've seen is:
			     FirebaseErrror: Firebase: A network AuthError (such as timeout, interrupted connection or unreachable host)
				 has occurred. (auth/network-request-failed)
			 */
			ev.preventDefault()
			return
		}
		
		platform.alert(`An unexpected error occurred, please send a screenshot of this error for support:\n\n${reason}\n\n${ev.reason.stack}`)
		ev.preventDefault()
		return
	}

	// if (reason.indexOf('FirebaseError') !== -1 && reason.indexOf('auth/network-request-failed')) {
	// 	/* This usually means the app is offline, and we'll detect that separately */
	// 	ev.preventDefault()
	// 	return
	// }

	platform.alert(`An unexpected error occurred, please send a screenshot of this error for support:\n\n${reason}`)
	
	ev.preventDefault()
})
